import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import platformAPI from "../../../services/platformApi";
import clinicAPI from "../../../services/clinicApi";
import { hasAdminAccess, isOwner } from "../../../utils/roles";
import { resolvePlanAccess } from "../../../utils/planEntitlements";
import { useToast } from "../../ui";
import { OwnerCabinetHubLink } from "../../Platform/OwnerCabinet/OwnerCabinetHubLink";
import ClubSubscriptionStatusHero from "../../common/ClubSubscription/ClubSubscriptionStatusHero";
import ClubSubscriptionAccessPanel from "../../common/ClubSubscription/ClubSubscriptionAccessPanel";
import ClubSubscriptionPlanPicker from "../../common/ClubSubscription/ClubSubscriptionPlanPicker";
import ss from "../../common/ClubSubscription/subscription.module.css";

export default function ProfileSubscriptionPanel({ billing }) {
  const toast = useToast();
  const { role, entitlements, tenant } = useSelector((state) => state.auth);
  const canPay = hasAdminAccess(role);
  const showOwnerHub = isOwner(role);
  const access = useMemo(
    () => resolvePlanAccess(entitlements, tenant, billing),
    [entitlements, tenant, billing],
  );

  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState(billing?.billing_period || "monthly");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isTrial = Boolean(billing?.is_trial && billing?.is_paid);

  const businessPlan = useMemo(
    () => plans.find((p) => p.code === "business") || null,
    [plans],
  );

  const currentPlanId = useMemo(() => {
    if (billing?.saas_plan_id) return billing.saas_plan_id;
    const byCode = plans.find((p) => p.code === billing?.plan_code);
    return byCode?.id || null;
  }, [billing, plans]);

  const clubName = billing?.club_name || tenant?.name || "клуб";

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((res) => setPlans(res.plans || []))
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (billing?.billing_period) setPeriod(billing.billing_period);
    if (currentPlanId) {
      setSelectedPlanId(currentPlanId);
      return;
    }
    if (isTrial && businessPlan) setSelectedPlanId(businessPlan.id);
  }, [billing, isTrial, businessPlan, currentPlanId]);

  useEffect(() => {
    if (!canPay || !selectedPlanId || !period) {
      setCheckout(null);
      return undefined;
    }
    let cancelled = false;
    setCheckoutLoading(true);
    clinicAPI
      .getTenantSubscriptionCheckout({
        saas_plan_id: selectedPlanId,
        crm_billing_period: period,
      })
      .then((res) => {
        if (!cancelled) setCheckout(res.checkout || null);
      })
      .catch(() => {
        if (!cancelled) setCheckout(null);
      })
      .finally(() => {
        if (!cancelled) setCheckoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canPay, selectedPlanId, period]);

  const payPlan = useCallback(
    async (planId) => {
      if (!canPay || !planId) return;
      setSelectedPlanId(planId);
      setPayingPlanId(planId);
      try {
        const payData = await clinicAPI.initTenantSubscriptionPayment({
          saas_plan_id: planId,
          crm_billing_period: period,
        });
        if (payData.payment_url && String(payData.payment_url).startsWith("http")) {
          window.location.assign(payData.payment_url);
          return;
        }
        toast.error("Ссылка на оплату не получена. Проверьте настройки ЮKassa.");
      } catch (e) {
        toast.error(e.response?.data?.error || "Онлайн-оплата недоступна");
      } finally {
        setPayingPlanId(null);
      }
    },
    [canPay, period, toast],
  );

  if (!billing) {
    return <p className={ss.pickerHint}>Загрузка подписки…</p>;
  }

  return (
    <div className={ss.subRoot}>
      {showOwnerHub ? <OwnerCabinetHubLink variant="compact" /> : null}
      <ClubSubscriptionStatusHero billing={billing} clubName={clubName} />
      <ClubSubscriptionAccessPanel access={access} billing={billing} />
      <ClubSubscriptionPlanPicker
        plans={plans}
        period={period}
        onPeriodChange={setPeriod}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onPay={payPlan}
        payingPlanId={payingPlanId}
        isTrial={isTrial}
        clubName={clubName}
        canPay={canPay}
        currentPlanId={currentPlanId}
        renewOnly={Boolean(billing.renew_only)}
        currentPeriod={billing.billing_period || "monthly"}
        hasActivePaidPeriod={Boolean(billing.has_active_paid_period)}
        checkout={checkout}
        checkoutLoading={checkoutLoading}
      />
    </div>
  );
}
